import { createContext, useContext } from "react";

export const MoneyCtx = createContext({ cur: "MXN", rate: 1 });
export const useMoney = () => {
  const { cur, rate } = useContext(MoneyCtx);
  const conv = (mxn) => (cur === "USD" ? Number(mxn || 0) / (rate || 1) : Number(mxn || 0));
  const fmt = (mxn, d = 0) => `${cur === "USD" ? "$" : "MX$"}${conv(mxn).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d })}`;
  const compact = (mxn) => {
    const n = conv(mxn); const a = Math.abs(n);
    const s = a >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : a >= 1e3 ? `${(n / 1e3).toFixed(a >= 1e5 ? 0 : 1)}k` : `${Math.round(n)}`;
    return `${cur === "USD" ? "$" : "MX$"}${s}`;
  };
  return { cur, rate, conv, fmt, compact, sym: cur === "USD" ? "$" : "MX$" };
};

export const MONTHS_ES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
export const periodLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  return `${MONTHS_ES[m - 1]} ${y}`;
};
export const shortDate = (iso) => (iso ? new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short" }) : "—");

export function Section({ id, num, title, desc, right, children, testid }) {
  return (
    <section id={id} className="space-y-3" data-testid={testid || `mr-section-${id}`}>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono-data text-[10px] tracking-widest text-zinc-500">{String(num).padStart(2, "0")}</div>
          <h2 className="font-heading text-xl font-bold">{title}</h2>
          {desc && <p className="text-xs text-zinc-500 mt-0.5 max-w-3xl">{desc}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

export function Card({ title, desc, children, className = "", testid }) {
  return (
    <div className={`border border-zinc-200 bg-white p-4 ${className}`} data-testid={testid}>
      {title && (
        <div className="mb-3">
          <div className="text-xs font-semibold text-zinc-800">{title}</div>
          {desc && <div className="text-[11px] text-zinc-500 mt-0.5">{desc}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

export function KPI({ label, value, sub, tone = "default", testid, small }) {
  const tones = {
    default: "border-zinc-200 bg-white text-zinc-900",
    primary: "border-[#002FA7]/30 bg-[#002FA7]/5 text-[#002FA7]",
    good: "border-emerald-200 bg-emerald-50 text-emerald-800",
    warn: "border-amber-200 bg-amber-50 text-amber-800",
    bad: "border-[#E0000F]/30 bg-[#E0000F]/5 text-[#E0000F]",
    muted: "border-zinc-200 bg-zinc-50 text-zinc-700",
  };
  return (
    <div className={`border p-3.5 ${tones[tone] || tones.default}`} data-testid={testid}>
      <div className="text-[10px] font-mono-data uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`numeric font-bold mt-1.5 ${small ? "text-lg" : "text-2xl"}`}>{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-0.5 numeric">{sub}</div>}
    </div>
  );
}

export function PctBar({ pct, color = "#002FA7", label }) {
  const w = Math.min(100, Math.max(0, Number(pct || 0)));
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden"><div className="h-full" style={{ width: `${w}%`, background: color }} /></div>
      <span className="text-[11px] numeric text-zinc-600 w-12 text-right">{label ?? `${Number(pct || 0).toFixed(1)}%`}</span>
    </div>
  );
}

export function Empty({ text = "Sin datos en este periodo." }) {
  return <div className="text-xs text-zinc-400 italic py-6 text-center dotted-bg" data-testid="mr-empty">{text}</div>;
}

export function Table({ cols, rows, keyFn, empty, testid, footer }) {
  return (
    <div className="border border-zinc-200 bg-white overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
          <tr>{cols.map((c) => <th key={c.key} className={`px-3 py-2 font-semibold ${c.align === "right" ? "text-right" : "text-left"}`}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={cols.length}><Empty text={empty} /></td></tr>}
          {rows.map((r, i) => (
            <tr key={keyFn ? keyFn(r, i) : i} className="border-t border-zinc-100 hover:bg-zinc-50/60">
              {cols.map((c) => <td key={c.key} className={`px-3 py-2 ${c.align === "right" ? "text-right numeric" : ""} ${c.cls || ""}`}>{c.render ? c.render(r, i) : r[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
        {footer && <tfoot><tr className="border-t-2 border-zinc-200 bg-zinc-50 font-semibold">{footer.map((f, i) => <td key={i} className={`px-3 py-2 ${cols[i]?.align === "right" ? "text-right numeric" : ""}`}>{f}</td>)}</tr></tfoot>}
      </table>
    </div>
  );
}

export function ChartTip({ active, payload, label, fmt }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-zinc-200 shadow-md p-2.5 text-xs min-w-[150px]">
      <div className="font-semibold text-zinc-900 mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-3 py-0.5">
          <span className="inline-flex items-center gap-1.5 text-zinc-600"><span className="w-2 h-2 rounded-sm" style={{ background: p.color || p.fill }} />{p.name}</span>
          <span className="numeric font-semibold">{fmt ? fmt(p.value, p.dataKey) : p.value}</span>
        </div>
      ))}
    </div>
  );
}
